import { buildEmployeeTriggersRequest } from 'pages/employee-reviews/complete/employeeReviewCompleteHelpers';
import {
    appSignOffMethodKey,
    emailSignOffMethodKey,
    onScreenSignOffMethodKey,
    physicalSignOffMethodKey,
    toTriggerRequest
} from 'pages/employee-reviews/shared';

export const makeChangeMessage = "Make a change before saving.";
export const managerSignatureRequiredMessage = "A manager signature is required for this review type.";
export const employeeSignatureRequiredMessage = "The employee must sign again because this review was signed on screen.";

function toComparableText(value) {
    const text = value ?? "";
    let end = text.length;

    while (end > 0 && text[end - 1] === " ") {
        end--;
    }

    return text.slice(0, end).toUpperCase();
}

function compareText(first, second) {
    return first.localeCompare(second);
}

// Whether an edited text is the one the review holds, following the rule the API applies when it
// decides what an edit changes: ignoring case and trailing spaces, with null and empty the same
// value. JavaScript's case mapping is broader than the API's ordinal one (ß matches SS here), so
// where the two differ this errs towards no change.
export function isSameText(first, second) {
    return toComparableText(first) === toComparableText(second);
}

// The values the Outcome step starts from, as the completed review recorded them.
export function getLoadedOutcome(review) {
    return {
        outcomeLevelNumber: review?.outcomeLevel?.number ?? null,
        outcomeSummary: review?.outcomeSummary ?? ""
    };
}

// A trigger as the API matches it: its type, topic and condition, which are what it sends.
function toComparableTrigger(trigger, reviewOptions) {
    const request = toTriggerRequest(trigger, reviewOptions);

    return JSON.stringify(Object.keys(request).sort(compareText).map((key) => [key, request[key]]));
}

function toComparableTriggerSet(triggers, reviewOptions) {
    return (triggers ?? []).map((trigger) => toComparableTrigger(trigger, reviewOptions)).sort(compareText);
}

// The driver's triggers are compared as the API compares them: by type, topic and condition, in any
// order. A trigger removed and added back unchanged, a reordered list, or a trigger whose added date
// alone differs, is therefore not a change.
export function areEmployeeTriggersChanged(employeeTriggers, loadedEmployeeTriggers, reviewOptions) {
    const current = toComparableTriggerSet(employeeTriggers, reviewOptions);
    const loaded = toComparableTriggerSet(loadedEmployeeTriggers, reviewOptions);

    return JSON.stringify(current) !== JSON.stringify(loaded);
}

function hasTopicChanges(topic, topicState) {
    return topic.isCommentChanged(topicState) || (topic.getItemEdits?.(topicState) ?? []).length > 0;
}

// A change to anything the employee signed off - the outcome, a topic comment or a topic's items -
// resets the sign-off. The driver's triggers are on no sign-off, so changing them alone does not.
export function hasSignOffResettingChanges({ review, topics, topicStates, outcomeLevelNumber, outcomeSummary }) {
    const loadedOutcome = getLoadedOutcome(review);

    return !isSameText(outcomeSummary, loadedOutcome.outcomeSummary)
        || (outcomeLevelNumber ?? null) !== loadedOutcome.outcomeLevelNumber
        || (topics ?? []).some((topic) => hasTopicChanges(topic, topicStates?.[topic.key]));
}

export function hasAnyChanges(answers) {
    return hasSignOffResettingChanges(answers)
        || (answers.review?.triggerSettings != null
            && areEmployeeTriggersChanged(answers.employeeTriggers, answers.loadedEmployeeTriggers, answers.reviewOptions));
}

// Saving is held until something has changed and, when the change resets the sign-off, until the
// signatures it calls for have been given: the manager's where the review type requires one, and
// the employee's again for a review signed on screen.
export function buildSaveGating(answers) {
    let tooltip = null;

    if (!hasAnyChanges(answers)) {
        tooltip = makeChangeMessage;
    } else if (hasSignOffResettingChanges(answers)) {
        if (answers.review?.signOff?.requireManagerSignature === true && answers.managerSignature == null) {
            tooltip = managerSignatureRequiredMessage;
        } else if (answers.review?.signOff?.method === onScreenSignOffMethodKey && answers.employeeSignature == null) {
            tooltip = employeeSignatureRequiredMessage;
        }
    }

    return {
        canProceed: tooltip == null,
        cantProceedTooltip: tooltip
    };
}

const physicalSaveConfirmationMessage = "Saving resets the employee's sign-off. The copy they signed no longer matches this review, so they will need to sign it again.";

// What saving a change that resets the sign-off does to the employee, for each method signed away
// from the page. A review with no sign-off method recorded is reset as a paper one is.
const saveConfirmationMessages = {
    [emailSignOffMethodKey]: "Saving resets the employee's sign-off and sends them the updated review by email.",
    [appSignOffMethodKey]: "The employee will need to sign again in the Vision app and will be sent a notification on their phone if they have notifications turned on.",
    [physicalSignOffMethodKey]: physicalSaveConfirmationMessage
};

// The Sign-off step is offered only when it collects a signature: the employee's again for a review
// signed on screen, or the manager's where the review type requires it. Both are known when the
// review loads, so the steps do not change while it is edited.
export function hasSignOffStep(review) {
    return review?.signOff?.method === onScreenSignOffMethodKey || review?.signOff?.requireManagerSignature === true;
}

// The message a save that resets the sign-off is confirmed with, or null when it is saved without
// one: a save that leaves the sign-off alone, or a review whose employee signs again on the page.
export function getSaveConfirmationMessage(answers) {
    const method = answers.review?.signOff?.method ?? null;

    if (method === onScreenSignOffMethodKey || !hasSignOffResettingChanges(answers)) {
        return null;
    }

    return saveConfirmationMessages[method] ?? physicalSaveConfirmationMessage;
}

// Builds the PUT body for editing a completed review. The outcome is always sent as it stands. A
// topic is listed only when its comment or its items changed, always with its comment as it
// stands, and carries the changed items when it has any. The driver's triggers are sent only when
// the review offers them and they differ from what was loaded. Each signature is sent only with a
// change that resets the sign-off: the employee's only for a review signed on screen.
export function buildEditReviewRequest(answers) {
    const {
        review,
        topics = [],
        topicStates = {},
        outcomeLevelNumber,
        outcomeSummary,
        employeeTriggers,
        loadedEmployeeTriggers,
        reviewOptions,
        employeeSignature,
        managerSignature
    } = answers;

    const request = {
        outcomeSummary: outcomeSummary ?? null,
        outcomeLevelNumber: outcomeLevelNumber ?? null
    };

    const changedTopics = topics
        .filter((topic) => hasTopicChanges(topic, topicStates[topic.key]))
        .map((topic) => {
            const topicState = topicStates[topic.key];
            const entry = { topicKey: topic.key, comment: topic.getComment(topicState) };
            const items = topic.getItemEdits?.(topicState) ?? [];

            if (items.length > 0) {
                entry.items = items;
            }

            return entry;
        });

    if (changedTopics.length > 0) {
        request.topics = changedTopics;
    }

    if (review?.triggerSettings != null && areEmployeeTriggersChanged(employeeTriggers, loadedEmployeeTriggers, reviewOptions)) {
        request.employeeTriggers = buildEmployeeTriggersRequest(review, employeeTriggers, reviewOptions);
    }

    if (hasSignOffResettingChanges(answers)) {
        if (review?.signOff?.method === onScreenSignOffMethodKey && employeeSignature != null) {
            request.employeeSignature = employeeSignature;
        }

        if (managerSignature != null) {
            request.managerSignature = managerSignature;
        }
    }

    return request;
}
